import { ADMIN_BASE_PATH } from '@/lib/admin-paths';
import { getMemoryAccessToken } from '@/lib/access-token-memory';

/** Canonical public host (Hub, one trailing b). */
export const PUBLIC_HOST = 'enthioshub.com';

/** Extra-b misspelling, built without a contiguous typo literal. */
export function typoPublicHost(): string {
  return PUBLIC_HOST.replace('hub.com', `hub${'b'}.com`);
}

/** Rewrite known public-host typos so share / QR links never encode them. */
export function canonicalizePublicUrl(url: string): string {
  if (!url) return url;
  let next = url;
  if (next.includes('127.0.0.1') || next.includes('localhost')) {
    next = next.replace(/https?:\/\/(127\.0\.0\.1|localhost)(:\d+)?/gi, `https://${PUBLIC_HOST}`);
  }
  const typo = typoPublicHost();
  const escaped = typo.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  next = next.replace(new RegExp(escaped, 'gi'), PUBLIC_HOST);
  // Missing "s" typo: enthiohub.com → enthioshub.com
  next = next.replace(/enthiohub\.com/gi, PUBLIC_HOST);
  return next;
}

export function getVerificationBaseUrl(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    const hostname = window.location.hostname;
    const isLoopback = ['localhost', '127.0.0.1', '[::1]'].includes(hostname);
    if (!isLoopback) {
      return window.location.origin.replace(/\/$/, '');
    }
  }
  const envUrl = (
    process.env.NEXT_PUBLIC_FRONTEND_URL ||
    process.env.NEXT_PUBLIC_APP_URL ||
    process.env.NEXT_PUBLIC_DASHBOARD_URL ||
    process.env.NEXT_PUBLIC_BASE_URL ||
    ''
  ).trim();
  if (envUrl && !envUrl.includes('127.0.0.1') && !envUrl.includes('localhost')) {
    return envUrl.replace(/\/$/, '');
  }
  return `https://${PUBLIC_HOST}`;
}

export function getLocalBrowserBaseUrl(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/$/, '');
  }
  const envUrl = (process.env.NEXT_PUBLIC_APP_URL || '').trim();
  if (envUrl) {
    return envUrl.replace(/\/$/, '');
  }
  return `http://localhost:3000`;
}

export function getPublicAppBaseUrl(): string {
  return getVerificationBaseUrl();
}

/** Canonical production API origin. Used only as last-resort on production hosts when env/runtime missing. */
export const PRODUCTION_API_BASE_URL = 'https://api.enthioshub.com/api';
export const PRODUCTION_BACKEND_URL = 'https://admin.enthioshub.com';
export const PRODUCTION_AUTH_URL = 'https://auth.enthioshub.com';

/** Canonical staging origins for fallback on staging hosts — never fall back to same-origin /api on these. */
export const STAGING_API_BASE_URL = 'https://stagingapi.enthioshub.com/api';
export const STAGING_BACKEND_URL = 'https://stagingadmin.enthioshub.com';
export const STAGING_AUTH_URL = 'https://stagingauth.enthioshub.com';

/** Local development defaults — never fall back to production from a local/dev process. */
export const LOCAL_API_BASE_URL =
  process.env.NODE_ENV === 'development' ? 'http://127.0.0.1:8000/api' : '/api';
export const LOCAL_BACKEND_URL = 'http://127.0.0.1:8000';

function bakedApiUrl(): string {
  const raw = (process.env.API_BASE_URL || process.env.NEXT_PUBLIC_API_URL || '').trim();
  return raw;
}

function bakedBackendUrl(): string {
  return (process.env.NEXT_PUBLIC_ADMIN_URL || process.env.NEXT_PUBLIC_BACKEND_URL || '').trim();
}

function bakedAuthUrl(): string {
  return (process.env.NEXT_PUBLIC_AUTH_URL || '').trim();
}

function bakedAppUrl(): string {
  return (process.env.NEXT_PUBLIC_FRONTEND_URL || process.env.NEXT_PUBLIC_APP_URL || process.env.NEXT_PUBLIC_DASHBOARD_URL || '').trim();
}

const API_BASE_URL_FALLBACK =
  bakedApiUrl() ||
  (process.env.NODE_ENV === 'development' ? LOCAL_API_BASE_URL : PRODUCTION_API_BASE_URL);

declare global {
  interface Window {
    __ENTHIOS_RUNTIME__?: {
      apiUrl?: string;
      backendUrl?: string;
      authUrl?: string;
    };
    /** Legacy alias used by some static deployments */
    RUNTIME_CONFIG?: {
      apiUrl?: string;
      backendUrl?: string;
      authUrl?: string;
    };
    /** Jest-only host override. Never set in production. */
    __ENTHIOS_TEST_HOSTNAME__?: string;
  }
}

function browserHostname(): string {
  if (typeof window === 'undefined') return '';
  const override = window.__ENTHIOS_TEST_HOSTNAME__;
  if (typeof override === 'string' && override.trim()) {
    return override.trim().toLowerCase();
  }
  return window.location.hostname;
}

function browserOrigin(): string {
  if (typeof window === 'undefined') return '';
  const override = window.__ENTHIOS_TEST_HOSTNAME__;
  if (typeof override === 'string' && override.trim()) {
    const host = override.trim().toLowerCase();
    return isLocalDevHostname(host) ? `http://${host}` : `https://${host}`;
  }
  return window.location.origin.replace(/\/$/, '');
}

function normalize(url: string): string {
  return url.replace(/\/$/, '');
}

function ensureApiSuffix(url: string): string {
  const normalized = normalize(url);
  return /\/api\/?$/i.test(normalized) ? normalized.replace(/\/$/, '') : `${normalized}/api`;
}

function parseJwtPayload(token: string): Record<string, unknown> | null {
  const parts = String(token || '').split('.');
  if (parts.length !== 3) return null;

  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = typeof window !== 'undefined'
      ? atob(padded)
      : Buffer.from(padded, 'base64').toString('utf8');
    const payload = JSON.parse(json);
    return payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function getJwtScope(token: string): string | null {
  const payload = parseJwtPayload(token);
  const scope = payload?.scope;
  return typeof scope === 'string' && scope.trim() ? scope.trim() : null;
}

function hasAdminAccessToken(): boolean {
  if (typeof window === 'undefined') return false;
  // Access JWT is in-memory / HttpOnly cookie — never trust localStorage or window globals.
  const memory = getMemoryAccessToken();
  if (!memory) return false;
  const scope = getJwtScope(memory);
  return scope === 'admin_access' || scope === 'admin';
}

function isLocalDevHostname(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return host === 'localhost' || host === '127.0.0.1' || host === '[::1]';
}

/** True when URL points at a loopback dev API (must never be used on production hosts). */
export function isDevApiUrl(url: string): boolean {
  const value = url.trim().toLowerCase();
  if (!value.startsWith('http://') && !value.startsWith('https://')) {
    return false;
  }

  try {
    const { hostname } = new URL(value);
    return isLocalDevHostname(hostname);
  } catch {
    return (
      value.includes('127.0.0.1') ||
      value.includes('localhost:8000') ||
      value.includes('localhost:3000')
    );
  }
}

function readRuntimeApiUrl(): string | null {
  if (typeof window === 'undefined') return null;

  const sources = [
    window.__ENTHIOS_RUNTIME__?.apiUrl,
    window.RUNTIME_CONFIG?.apiUrl,
  ];

  for (const raw of sources) {
    if (typeof raw !== 'string' || !raw.trim()) continue;
    return ensureApiSuffix(raw.trim());
  }

  return null;
}

function readRuntimeBackendUrl(): string | null {
  if (typeof window === 'undefined') return null;

  const sources = [
    window.__ENTHIOS_RUNTIME__?.backendUrl,
    window.RUNTIME_CONFIG?.backendUrl,
  ];

  for (const raw of sources) {
    if (typeof raw !== 'string' || !raw.trim()) continue;
    return normalize(raw.trim());
  }

  return null;
}

function readRuntimeAuthUrl(): string | null {
  if (typeof window === 'undefined') return null;

  const sources = [
    window.__ENTHIOS_RUNTIME__?.authUrl,
    window.RUNTIME_CONFIG?.authUrl,
  ];

  for (const raw of sources) {
    if (typeof raw !== 'string' || !raw.trim()) continue;
    return normalize(raw.trim());
  }

  return null;
}

export function isMainMarketingHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return (
    host === 'enthioshub.com' ||
    host === 'www.enthioshub.com' ||
    host.startsWith('stagingfrontend.')
  );
}

export function isStagingFrontendHost(hostname: string): boolean {
  return hostname.toLowerCase().startsWith('stagingfrontend.');
}

export function isStagingAdminHost(hostname: string): boolean {
  return hostname.toLowerCase().startsWith('stagingadmin.');
}

/** Any staging*.enthioshub.com host (frontend/admin/api/auth). */
export function isStagingEnthiosHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return host.endsWith('.enthioshub.com') && host.startsWith('staging');
}

function isProductionEnthiosHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return (
    isMainMarketingHost(host) ||
    host === 'admin.enthioshub.com' ||
    (host.endsWith('.enthioshub.com') && !host.startsWith('staging'))
  );
}

function defaultApiBaseForHost(hostname: string): string {
  if (isStagingEnthiosHost(hostname)) {
    return STAGING_API_BASE_URL;
  }
  return PRODUCTION_API_BASE_URL;
}

function defaultBackendUrlForHost(hostname: string): string {
  if (isStagingEnthiosHost(hostname)) {
    return STAGING_BACKEND_URL;
  }
  return PRODUCTION_BACKEND_URL;
}

function defaultAuthUrlForHost(hostname: string): string {
  if (isStagingEnthiosHost(hostname)) {
    return STAGING_AUTH_URL;
  }
  return PRODUCTION_AUTH_URL;
}

function isStagingApiUrl(url: string): boolean {
  return /staging[a-z0-9-]*\.enthioshub\.com/i.test(url);
}

function isStagingServiceUrl(url: string): boolean {
  return isStagingApiUrl(url);
}

function isProductionApiUrl(url: string): boolean {
  return /(?:^|\/\/)(?:api|admin)\.enthioshub\.com/i.test(url) && !isStagingApiUrl(url);
}

/** Apex / production-only hosts (excludes staging*). */
export function isCanonicalProductionHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return (
    host === 'enthioshub.com' ||
    host === 'www.enthioshub.com' ||
    host === 'admin.enthioshub.com' ||
    host === 'api.enthioshub.com' ||
    host === 'auth.enthioshub.com'
  );
}

/**
 * Static export on enthioshub.com serves POST /api/* as index.html unless the client
 * calls the Laravel host. Never use loopback URLs when the page is on a public host.
 *
 * On localhost: prefer an explicit local env URL (so .env.development.local wins over a
 * stale production runtime-config.js), then runtime, then any other baked URL.
 * Never fall back to the production API from a local hostname.
 *
 * Staging hosts always use the staging API (even if a production runtime-config was uploaded).
 * Production hosts always use the production API. Public staging never uses same-origin /api.
 */
function resolveBrowserApiBaseUrl(fallback: string): string {
  const hostname = browserHostname();

  if (isLocalDevHostname(hostname)) {
    return `${browserOrigin()}/api`;
  }

  // Staging ↔ staging API. Host wins over a production runtime-config.
  if (isStagingEnthiosHost(hostname)) {
    const runtime = readRuntimeApiUrl();
    if (runtime && isStagingApiUrl(runtime) && !isDevApiUrl(runtime)) {
      return runtime;
    }
    const baked = bakedApiUrl();
    if (baked && isStagingApiUrl(baked) && !isDevApiUrl(baked)) {
      return ensureApiSuffix(baked);
    }
    return STAGING_API_BASE_URL;
  }

  // Production hosts must never call staging APIs.
  if (isCanonicalProductionHost(hostname)) {
    const runtime = readRuntimeApiUrl();
    if (runtime && isProductionApiUrl(runtime) && !isDevApiUrl(runtime) && !isStagingApiUrl(runtime)) {
      return ensureApiSuffix(runtime);
    }
    const baked = bakedApiUrl();
    if (baked && isProductionApiUrl(baked) && !isDevApiUrl(baked) && !isStagingApiUrl(baked)) {
      return ensureApiSuffix(baked);
    }
    return PRODUCTION_API_BASE_URL;
  }

  const runtime = readRuntimeApiUrl();
  if (runtime && (!isDevApiUrl(runtime) || isLocalDevHostname(hostname))) {
    return runtime;
  }

  const baked = typeof process.env.NEXT_PUBLIC_API_URL === 'string'
    ? process.env.NEXT_PUBLIC_API_URL.trim()
    : '';
  if (baked && (!isDevApiUrl(baked) || isLocalDevHostname(hostname))) {
    return ensureApiSuffix(baked);
  }

  if (!fallback.startsWith('http://') && !fallback.startsWith('https://')) {
    if (isProductionEnthiosHost(hostname)) {
      return defaultApiBaseForHost(hostname);
    }
    return fallback;
  }

  if (isDevApiUrl(fallback) && !isLocalDevHostname(hostname)) {
    return defaultApiBaseForHost(hostname);
  }

  if (isMainMarketingHost(hostname)) {
    return defaultApiBaseForHost(hostname);
  }

  return ensureApiSuffix(fallback);
}

/**
 * Absolute URL to the backend API.
 */
function isLocalDevBuild(): boolean {
  return process.env.NODE_ENV === 'development';
}

export function getApiBaseUrl(): string {
  if (typeof window !== 'undefined') {
    return resolveBrowserApiBaseUrl(normalize(API_BASE_URL_FALLBACK));
  }

  if (isLocalDevBuild()) {
    const apiBase = process.env.API_BASE_URL;
    if (typeof apiBase === 'string' && apiBase.trim()) {
      return ensureApiSuffix(apiBase.trim());
    }

    const publicApi = process.env.NEXT_PUBLIC_API_URL;
    if (typeof publicApi === 'string' && publicApi.trim()) {
      const trimmed = publicApi.trim();
      if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
        return ensureApiSuffix(trimmed);
      }
      // Relative /api in local SSR → local Laravel
      return LOCAL_API_BASE_URL;
    }

    return LOCAL_API_BASE_URL;
  }

  const url = process.env.NEXT_PUBLIC_API_URL;
  const trimmed = typeof url === 'string' ? url.trim() : '';
  if (trimmed) {
    return isDevApiUrl(trimmed) ? PRODUCTION_API_BASE_URL : ensureApiSuffix(trimmed);
  }

  const fallback = normalize(API_BASE_URL_FALLBACK);
  if (!fallback.startsWith('http') || isDevApiUrl(fallback)) {
    return PRODUCTION_API_BASE_URL;
  }

  return ensureApiSuffix(fallback);
}

/**
 * Absolute URL to an API path (no leading slash on segment after base).
 */
export function getApiUrl(relativePath: string): string {
  const p = relativePath.replace(/^\//, '');
  const base = getApiBaseUrl();
  if (base.startsWith('http://') || base.startsWith('https://')) {
    return `${base.replace(/\/$/, '')}/${p}`;
  }
  const root = base.startsWith('/') ? base : `/${base}`;
  if (typeof window !== 'undefined') {
    return `${window.location.origin.replace(/\/$/, '')}${root.replace(/\/$/, '')}/${p}`;
  }
  return `${root.replace(/\/$/, '')}/${p}`;
}

/**
 * Origin (base URL without /api) for building asset/image URLs.
 */
export function getOrigin(): string {
  return getApiBaseUrl().replace(/\/api\/?$/, '');
}

export function getCurrentPathname(): string {
  if (typeof window === 'undefined') return '';
  return window.location.pathname || '';
}

/** True when the current URL path is under the admin portal. */
export function isAdminPath(pathname?: string): boolean {
  const path = (pathname ?? getCurrentPathname()).replace(/\/$/, '') || '/';
  return (
    path === ADMIN_BASE_PATH ||
    path.startsWith(`${ADMIN_BASE_PATH}/`) ||
    path === '/admin' ||
    path.startsWith('/admin/')
  );
}

/** Dedicated admin sign-in route (before redirect to /login?admin=1). */
function isAdminLoginPath(pathname?: string): boolean {
  const path = (pathname ?? getCurrentPathname()).replace(/\/$/, '') || '/';
  return path === '/admin/login' || path.startsWith('/admin/login/');
}

/** Explicit admin login query flags on /login. */
function isAdminLoginContext(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get('admin') === '1') return true;
  const reason = params.get('reason');
  if (reason === 'two_factor') return true;
  // Pending OTP challenge must keep using /admin/login (+ /admin/login/2fa), never /auth/login.
  try {
    const raw = sessionStorage.getItem('admin:2fa-login-challenge');
    if (raw) {
      const parsed = JSON.parse(raw) as { challengeToken?: string };
      if (typeof parsed?.challengeToken === 'string' && parsed.challengeToken.trim().length >= 32) {
        return true;
      }
    }
  } catch {
    // ignore storage errors
  }
  return false;
}

/** True when ?redirect= points at the admin dashboard (e.g. unauthenticated /dashboard/admin). */
function isAdminLoginRedirectTarget(): boolean {
  if (typeof window === 'undefined') return false;
  const redirect = new URLSearchParams(window.location.search).get('redirect')?.trim();
  if (!redirect) return false;

  const path = redirect.split('?')[0].replace(/\/$/, '') || '/';
  return (
    path === ADMIN_BASE_PATH ||
    path.startsWith(`${ADMIN_BASE_PATH}/`) ||
    path === '/admin' ||
    path.startsWith('/admin/')
  );
}

/**
 * Hostname-only admin portal detection (testable without window.location mocks).
 * Public /login on marketing or localhost hosts still uses platform auth unless query/path flags apply.
 */
export function isAdminPortalHostForHostname(
  hostname: string,
  configuredAdminUrl?: string | null
): boolean {
  const host = hostname.toLowerCase();

  if (isMainMarketingHost(host)) {
    return false;
  }

  // Production admin host, or any host whose first label is exactly "admin".
  if (host === 'admin.enthioshub.com' || /^admin\./.test(host)) {
    return true;
  }

  // Staging admin host (stagingadmin.*)
  if (isStagingAdminHost(host)) {
    return true;
  }

  if (isLocalDevHostname(host)) {
    return false;
  }

  const configuredAdmin = configuredAdminUrl?.trim();
  if (!configuredAdmin) {
    return false;
  }

  try {
    const configuredHost = new URL(configuredAdmin).hostname.toLowerCase();
    if (isMainMarketingHost(configuredHost) || isLocalDevHostname(configuredHost)) {
      return false;
    }
    return configuredHost === host;
  } catch {
    return false;
  }
}

/** True on the dedicated admin app host (e.g. admin.enthioshub.com). */
export function isAdminPortalHost(): boolean {
  if (typeof window === 'undefined') return false;
  return isAdminPortalHostForHostname(
    window.location.hostname,
    process.env.NEXT_PUBLIC_ADMIN_URL || process.env.NEXT_PUBLIC_ADMIN_APP_URL || process.env.NEXT_PUBLIC_BACKEND_URL
  );
}

/**
 * Use admin login API on admin portal entry points:
 * - admin app host (e.g. admin.enthioshub.com)
 * - /admin/login
 * - /login?admin=1 or 2FA continuation (reason=two_factor)
 * - /login?redirect=/dashboard/admin (or other admin paths)
 *
 * Public /login on the main marketing host still uses POST /api/auth/login.
 */
export function shouldUseAdminLogin(pathname?: string): boolean {
  if (isAdminPortalHost()) return true;
  if (isAdminLoginPath(pathname)) return true;
  if (isAdminLoginContext()) return true;
  if (isAdminLoginRedirectTarget()) return true;
  return false;
}

/**
 * Use admin auth API when on admin routes, admin login flow, or holding an admin JWT.
 * Localhost admin paths must count even without a readable JWT (HttpOnly cookies).
 */
export function shouldUseAdminAuth(pathname?: string): boolean {
  if (shouldUseAdminLogin(pathname)) return true;
  if (isAdminPath(pathname)) return true;
  return hasAdminAccessToken();
}

/** Backend base URL for redirects (Blade dashboards). */
export function getBackendUrl(): string {
  if (typeof window !== 'undefined') {
    const hostname = browserHostname();

    if (isLocalDevHostname(hostname)) {
      const bakedLocal = typeof process.env.NEXT_PUBLIC_BACKEND_URL === 'string'
        ? process.env.NEXT_PUBLIC_BACKEND_URL.trim()
        : '';
      if (bakedLocal && isDevApiUrl(bakedLocal)) {
        return bakedLocal.replace(/\/$/, '');
      }
      return LOCAL_BACKEND_URL;
    }

    if (isStagingEnthiosHost(hostname)) {
      const runtime = readRuntimeBackendUrl();
      if (runtime && isStagingServiceUrl(runtime)) {
        return runtime;
      }
      const baked = bakedBackendUrl();
      if (baked && isStagingServiceUrl(baked)) {
        return baked.replace(/\/$/, '');
      }
      return defaultBackendUrlForHost(hostname);
    }

    if (isCanonicalProductionHost(hostname)) {
      const runtime = readRuntimeBackendUrl();
      if (runtime && isProductionApiUrl(runtime) && !isStagingServiceUrl(runtime)) {
        return runtime;
      }
      const baked = bakedBackendUrl();
      if (baked && !isDevApiUrl(baked) && !isStagingServiceUrl(baked)) {
        return baked.replace(/\/$/, '');
      }
      return PRODUCTION_BACKEND_URL;
    }

    const runtime = readRuntimeBackendUrl();
    if (runtime) {
      return runtime;
    }

    const baked = typeof process.env.NEXT_PUBLIC_BACKEND_URL === 'string'
      ? process.env.NEXT_PUBLIC_BACKEND_URL.trim()
      : '';
    if (baked) {
      return baked.replace(/\/$/, '');
    }

    if (isProductionEnthiosHost(hostname)) {
      return defaultBackendUrlForHost(hostname);
    }
  }

  if (isLocalDevBuild()) {
    const url = process.env.NEXT_PUBLIC_BACKEND_URL;
    const trimmed = typeof url === 'string' ? url.trim() : '';
    if (trimmed) return trimmed.replace(/\/$/, '');
    return LOCAL_BACKEND_URL;
  }

  const url = process.env.NEXT_PUBLIC_BACKEND_URL;
  const trimmed = typeof url === 'string' ? url.trim() : '';
  if (trimmed && (isLocalDevBuild() || !isDevApiUrl(trimmed))) {
    return trimmed.replace(/\/$/, '');
  }

  return getOrigin();
}

/**
 * Auth service origin (login/register/OTP UI host or branded auth subdomain).
 * API auth routes still go through getApiBaseUrl(); this is for redirects / branded auth links.
 */
export function getAuthUrl(): string {
  if (typeof window !== 'undefined') {
    const hostname = browserHostname();

    if (isLocalDevHostname(hostname)) {
      const bakedLocal = typeof process.env.NEXT_PUBLIC_AUTH_URL === 'string'
        ? process.env.NEXT_PUBLIC_AUTH_URL.trim()
        : '';
      if (bakedLocal && isDevApiUrl(bakedLocal)) {
        return bakedLocal.replace(/\/$/, '');
      }
      return LOCAL_BACKEND_URL;
    }

    if (isStagingEnthiosHost(hostname)) {
      const runtime = readRuntimeAuthUrl();
      if (runtime && isStagingServiceUrl(runtime)) {
        return runtime;
      }
      const baked = bakedAuthUrl();
      if (baked && isStagingServiceUrl(baked)) {
        return baked.replace(/\/$/, '');
      }
      return defaultAuthUrlForHost(hostname);
    }

    if (isCanonicalProductionHost(hostname)) {
      const runtime = readRuntimeAuthUrl();
      if (runtime && /(?:^|\/\/)auth\.enthioshub\.com/i.test(runtime) && !isStagingServiceUrl(runtime)) {
        return runtime;
      }
      const baked = bakedAuthUrl();
      if (baked && !isDevApiUrl(baked) && !isStagingServiceUrl(baked)) {
        return baked.replace(/\/$/, '');
      }
      return PRODUCTION_AUTH_URL;
    }

    const runtime = readRuntimeAuthUrl();
    if (runtime) {
      return runtime;
    }

    const baked = typeof process.env.NEXT_PUBLIC_AUTH_URL === 'string'
      ? process.env.NEXT_PUBLIC_AUTH_URL.trim()
      : '';
    if (baked) {
      return baked.replace(/\/$/, '');
    }

    if (isProductionEnthiosHost(hostname)) {
      return defaultAuthUrlForHost(hostname);
    }
  }

  if (isLocalDevBuild()) {
    const url = process.env.NEXT_PUBLIC_AUTH_URL;
    const trimmed = typeof url === 'string' ? url.trim() : '';
    if (trimmed) return trimmed.replace(/\/$/, '');
    return LOCAL_BACKEND_URL;
  }

  const url = process.env.NEXT_PUBLIC_AUTH_URL;
  const trimmed = typeof url === 'string' ? url.trim() : '';
  if (trimmed && (isLocalDevBuild() || !isDevApiUrl(trimmed))) {
    return trimmed.replace(/\/$/, '');
  }

  return getBackendUrl();
}

/**
 * Dashboard origin used by frontend redirect helpers.
 */
export function getDashboardOrigin(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/$/, '');
  }
  const trimmed = bakedAppUrl();
  if (trimmed) return trimmed.replace(/\/$/, '');
  return getBackendUrl();
}
